import { Box } from "@mui/material";
import { OLPaper } from "OLPaper";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useStores } from "api/stores";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { FormButtons } from "components/FormControls/FormButtons";
import { OLCheckbox } from "components/FormControls/OLCheckbox";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useNavigate } from "react-router-dom";
import {
  selectCurrentStoreData,
  setCurrentStoreData,
} from "reduxState/currentStoreData/currentStoreDataSlice";
import { getCurrencies } from "reduxState/settings/settings";
import { StoreData } from "types/stores";
import { alphabetizeByProperty } from "utilities/arrays";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { useStyles } from "./StoreForm.styles";

export function StoreForm(props: {
  editMode?: boolean;
  initialValues?: StoreData;
}) {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { notify } = useNotifications();
  const { addStore, editStore } = useStores();
  const dispatch = useAppDispatch();
  const currencies = useAppSelector(getCurrencies);
  const store = useAppSelector(selectCurrentStoreData);

  const initialValues =
    props.editMode && props.initialValues
      ? {
          active: props.initialValues.active,
          code: props.initialValues.code,
          currency: props.initialValues.currency,
          name: props.initialValues.name,
          storeId: props.initialValues.storeId,
        }
      : {
          active: false,
          code: "",
          currency: "",
          name: "",
        };

  return (
    <>
      {((props.editMode && props.initialValues) || !props.editMode) && (
        <Formik
          enableReinitialize={props.editMode}
          initialStatus={{}}
          initialValues={initialValues}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            const isEditingCurrentStore = store.id === values.storeId;

            setSubmitting(true);
            setStatus({});
            try {
              props.editMode && props.initialValues && values.storeId
                ? await editStore({
                    storeId: values.storeId,
                    active: values.active,
                    name: values.name,
                  })
                : await addStore(values);
              navigate(links.settings);
              notify(
                message({
                  id: props.editMode ? "tenantUpdated" : "tenantAdded",
                }),
              );

              if (props.editMode && isEditingCurrentStore) {
                dispatch(
                  setCurrentStoreData({
                    ...store,
                    name: values.name,
                  }),
                );
              }
            } catch (exception) {
              setSubmitting(false);
              notify(getErrorMessage(exception));
              const errors = backendErrors(getBackendErrors(exception));
              setStatus(errors);
              focusError();
            }
          }}
        >
          {({
            handleSubmit,
            isSubmitting,
            status,
            dirty,
            values,
            initialValues,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                {status?.[""] && (
                  <span className={classes.error}>{status[""]}</span>
                )}
                <OLPaper className={classes.OLPaper}>
                  <SelectInput
                    className={classes.SelectInput}
                    data-test-id="tenant-currency-select"
                    displaySearch
                    disabled={props.editMode}
                    items={
                      props.editMode
                        ? [
                            {
                              id: props.initialValues?.currency,
                              label: props.initialValues?.currency,
                            },
                          ]
                        : alphabetizeByProperty(
                            currencies.map(({ code }) => ({
                              id: code,
                              label: code,
                            })),
                            "label",
                          )
                    }
                    label={
                      <RequiredFieldLabel text={message({ id: "currency" })} />
                    }
                    name="currency"
                  />
                  <TextInput
                    className={classes.SelectInput}
                    data-test-id="tenant-code-input"
                    disabled={props.editMode}
                    label={
                      <RequiredFieldLabel text={message({ id: "code" })} />
                    }
                    name="code"
                  />
                  <TextInput
                    className={classes.SelectInput}
                    data-test-id="tenant-name-input"
                    label={
                      <RequiredFieldLabel text={message({ id: "name" })} />
                    }
                    name="name"
                  />
                  <OLCheckbox
                    className={`${classes.checkbox} ${classes.SelectInput}`}
                    data-test-id="active"
                    name="active"
                    label={message({ id: "active" })}
                  />
                </OLPaper>
                {!isSubmitting && (
                  <LeaveSiteGuard
                    warnBeforeRefresh
                    isDirty={dirty}
                    initialValues={initialValues}
                    currentValues={values}
                  />
                )}
                <Box mt={12} />
                <FormButtons
                  disableSubmit={isSubmitting}
                  onCancelClick={() => navigate(links.settings)}
                  onSubmitClick={() => handleSubmit()}
                  {...(!props.editMode && { submitMessage: "addTenant" })}
                />
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
}
